import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Modal, Paper, Button, Typography, Box, Grid, Link } from "@material-ui/core";
import { sendVerificationEmail, reloadUser } from "utils/firebase/firebase";
import { signInUser } from "features/user/userSlice";
import { useProcessSocialSignInResponse, useSignOutUser } from "hooks";
import LoadingSpinner from "components/LoadingSpinner";

const MOLLIE_MEMBER_TITLE = process.env.REACT_APP_MOLLIES_MEMBER_TITLE;

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    padding: "2rem",
    borderRadius: "5px",
    wordWrap: "break-word",
    width: "90%",
    maxWidth: "700px",
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      width: "50%",
    },
  },
}));

const ConfirmEmailModal = (props) => {
  const classes = useStyles();
  const authUser = useSelector((state) => state.user.authUser);
  const dispatch = useDispatch();
  const signOutUser = useSignOutUser();
  const { processSocialSignInResponse } = useProcessSocialSignInResponse();
  const [loading, setLoading] = useState(false);

  const handleUserSignOut = () => {
    signOutUser();
  };

  const handleSendEmailVerification = async (onNotifOpen) => {
    setLoading(true);
    await sendVerificationEmail(onNotifOpen);
    setLoading(false);
  };

  const confirmEmailVerification = async () => {
    setLoading(true);
    const response = await reloadUser();
    const genericErrorMessage =
      "Please click the verify link in the email we sent you to continue.\nIf you haven't received an email, check your junk folder.";
    try {
      await processSocialSignInResponse(response?.token);
    } catch (error) {
      props.onNotifOpen(genericErrorMessage, { variant: "error" });
      setLoading(false);
      return;
    }

    if (response.verified) {
      props.onNotifOpen("Email verified successfully!", { variant: "success" });
      dispatch(signInUser({ ...authUser, emailVerified: response.verified }));
      props.onSuccess();
    } else {
      props.onNotifOpen(genericErrorMessage, { variant: "error" });
    }
    setLoading(false);
  };

  const confirmEmailCtaButtons = [
    <Button
      key="verification"
      variant="outlined"
      color="primary"
      onClick={() => handleSendEmailVerification(props.onNotifOpen)}>
      Resend verification email
    </Button>,
    <Button key="continue" variant="contained" color="primary" onClick={confirmEmailVerification}>
      Continue
    </Button>,
  ];

  return (
    <Modal open={props.open} className={classes.modal}>
      <Paper className={classes.paper}>
        <>
          <Box mb={3}>
            <Typography variant="h6" color="textPrimary" align="center">
              Please Verify Your Email
            </Typography>
          </Box>
          <Box mb={2}>
            <Box mb={1}>
              <Typography variant="body1" color="textPrimary" mb={6}>
                {`We've sent a verification email to ${authUser?.email || "your email address"}.`}
              </Typography>
            </Box>
            <Typography variant="body1" color="textPrimary">
              {`Please check your inbox and click the verification link to complete your registration and join ${MOLLIE_MEMBER_TITLE}.`}
            </Typography>
          </Box>
          <Box mb={3}>
            <Link component="button" variant="body2" onClick={handleUserSignOut}>
              Not you? Sign Out
            </Link>
          </Box>

          <Grid spacing={2} container justifyContent="flex-end">
            {loading ? (
              <LoadingSpinner loading={loading} />
            ) : (
              confirmEmailCtaButtons.map((button, i) => (
                <Grid item key={i}>
                  {button}
                </Grid>
              ))
            )}
          </Grid>
        </>
      </Paper>
    </Modal>
  );
};

ConfirmEmailModal.propTypes = {
  open: PropTypes.bool,
  onNotifOpen: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default ConfirmEmailModal;
